import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

const RoleCustomPaymentTypeAccessSection = ({
  roleOrganizationId,
  customPaymentTypeAccess,
  loading,
  error,
  onChange = () => {},
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity='error'>{error}</Alert>;
  }

  if (!customPaymentTypeAccess) {
    return (
      <Alert severity='warning'>Custom payment type access not found.</Alert>
    );
  }

  const options = customPaymentTypeAccess.custom_payment_types || [];
  const selectedIds =
    customPaymentTypeAccess.selected_custom_payment_type_ids || [];
  const selectedOptions = options.filter((option) =>
    selectedIds.includes(option.id),
  );
  const hasOrganizationContext = Boolean(roleOrganizationId);
  const isEnabled = customPaymentTypeAccess.custom_payment_type_access_enabled;

  const helperText = !hasOrganizationContext
    ? 'Select an organization on the Basic Information tab before configuring custom payment type access.'
    : isEnabled && selectedIds.length === 0
    ? 'This role will only see records without a custom payment type until you select allowed types.'
    : 'Selections stay saved even when access is disabled.';

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant='h6' gutterBottom>
          Custom Payment Type Access
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Restrict this role to specific custom payment types in its
          organization.
        </Typography>
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={isEnabled}
            onChange={(event) =>
              onChange({
                ...customPaymentTypeAccess,
                custom_payment_type_access_enabled: event.target.checked,
              })
            }
            disabled={!hasOrganizationContext}
          />
        }
        label='Enable custom payment type access restrictions'
      />

      <Box>
        <Typography variant='subtitle2' sx={{ mb: 1 }}>
          Selected custom payment types: {selectedIds.length}
        </Typography>
        <Autocomplete
          multiple
          size='small'
          disableCloseOnSelect
          options={options}
          value={selectedOptions}
          disabled={!hasOrganizationContext}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.name}
          onChange={(_event, value) => {
            onChange({
              ...customPaymentTypeAccess,
              selected_custom_payment_type_ids: value.map(
                (option) => option.id,
              ),
            });
          }}
          renderOption={(props, option) => (
            <Box component='li' {...props} sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ flex: 1 }}>{option.name}</Box>
              {option.is_disabled && (
                <Chip size='small' color='warning' label='Disabled' />
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              label='Allowed Custom Payment Types'
              placeholder='Search custom payment types'
              helperText={helperText}
            />
          )}
        />
      </Box>

      {!isEnabled && (
        <Alert severity='info'>
          Custom payment type access is currently disabled. Saved selections
          remain on the role and will apply again if you re-enable this feature.
        </Alert>
      )}
    </Stack>
  );
};

export default RoleCustomPaymentTypeAccessSection;
